import {RefObject, useCallback, useEffect, useRef, useState} from 'react';

export const SCROLL_DIRECTION = {
  UP: 'UP',
  DOWN: 'DOWN',
  STATIC: 'STATIC',
} as const;

export type ScrollDirection =
  (typeof SCROLL_DIRECTION)[keyof typeof SCROLL_DIRECTION];

interface UseScrollDirectionOptions {
  threshold?: number;
  hideDistance?: number;
  showDistance?: number;
  minUpdateIntervalMs?: number;
}

const DEFAULT_THRESHOLD = 10;
const DEFAULT_HIDE_DISTANCE = 60;
const DEFAULT_SHOW_DISTANCE = 30;
const DEFAULT_MIN_UPDATE_INTERVAL_MS = 500;

// Returns true when a negative scrollTop delta is caused by a layout shift
// rather than intentional user scrolling, so we can ignore it.
// Two cases:
//   1. scrollHeight grew → virtualizer added rows and corrected position
//   2. already hidden + at bottom → header collapse enlarged the container
function isLayoutArtifact(
  scrollDelta: number,
  scrollHeightGrew: boolean,
  distanceFromBottom: number,
  threshold: number,
  currentDirection: ScrollDirection
): boolean {
  if (scrollDelta >= 0) {
    return false;
  }
  if (scrollHeightGrew) {
    return true;
  }
  return (
    distanceFromBottom <= threshold &&
    currentDirection === SCROLL_DIRECTION.DOWN
  );
}

/**
 * Detects scroll direction with hysteresis.
 * Returns the current scroll direction: 'UP', 'DOWN', or 'STATIC'.
 */
function useScrollDirection(
  scrollContainerRef: RefObject<HTMLElement | null>,
  options: UseScrollDirectionOptions = {}
): ScrollDirection {
  const {
    threshold = DEFAULT_THRESHOLD,
    hideDistance = DEFAULT_HIDE_DISTANCE,
    showDistance = DEFAULT_SHOW_DISTANCE,
    minUpdateIntervalMs = DEFAULT_MIN_UPDATE_INTERVAL_MS,
  } = options;

  const [direction, setDirection] = useState<ScrollDirection>(
    SCROLL_DIRECTION.STATIC
  );
  const directionRef = useRef<ScrollDirection>(SCROLL_DIRECTION.STATIC);
  const lastScrollTop = useRef(0);
  const lastScrollHeight = useRef(0);
  const accumulatedScroll = useRef(0);
  const isMountedRef = useRef(true);
  const lastUpdateTime = useRef(0);

  const updateDirection = useCallback(
    (newDirection: ScrollDirection) => {
      if (!isMountedRef.current) {
        return;
      }

      const now = Date.now();
      const timeSinceLastUpdate = now - lastUpdateTime.current;
      const isDirectionChanging = directionRef.current !== newDirection;

      // Prevent rapid toggling by enforcing minimum time between state changes
      if (timeSinceLastUpdate < minUpdateIntervalMs && isDirectionChanging) {
        return;
      }

      lastUpdateTime.current = now;
      directionRef.current = newDirection;
      setDirection(newDirection);
    },
    [minUpdateIntervalMs]
  );

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return undefined;
    }

    isMountedRef.current = true;
    lastScrollHeight.current = scrollContainer.scrollHeight;
    let rafId: number | null = null;

    const handleScroll = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }

      rafId = requestAnimationFrame(() => {
        const currentScrollTop = scrollContainer.scrollTop;
        const currentScrollHeight = scrollContainer.scrollHeight;
        const scrollDelta = currentScrollTop - lastScrollTop.current;
        const scrollHeightGrew = currentScrollHeight > lastScrollHeight.current;
        lastScrollHeight.current = currentScrollHeight;

        // Near top: always static (bypass throttle so header reveals immediately)
        if (currentScrollTop <= threshold) {
          if (directionRef.current !== SCROLL_DIRECTION.STATIC) {
            lastUpdateTime.current = 0; // reset throttle so updateDirection won't block
            updateDirection(SCROLL_DIRECTION.STATIC);
          }
          accumulatedScroll.current = 0;
          lastScrollTop.current = currentScrollTop;
          return;
        }

        const distanceFromBottom =
          currentScrollHeight - scrollContainer.clientHeight - currentScrollTop;
        if (
          isLayoutArtifact(
            scrollDelta,
            scrollHeightGrew,
            distanceFromBottom,
            threshold,
            directionRef.current
          )
        ) {
          accumulatedScroll.current = 0;
          lastScrollTop.current = currentScrollTop;
          return;
        }

        const isScrollingDown = scrollDelta > 0;
        const wasScrollingDown = accumulatedScroll.current >= 0;

        // Direction changed: reset
        if (isScrollingDown !== wasScrollingDown) {
          accumulatedScroll.current = 0;
        }

        accumulatedScroll.current += scrollDelta;

        // Check thresholds
        if (
          directionRef.current !== SCROLL_DIRECTION.DOWN &&
          accumulatedScroll.current > hideDistance
        ) {
          updateDirection(SCROLL_DIRECTION.DOWN);
          accumulatedScroll.current = 0;
        } else if (
          directionRef.current !== SCROLL_DIRECTION.UP &&
          accumulatedScroll.current < -showDistance
        ) {
          updateDirection(SCROLL_DIRECTION.UP);
          accumulatedScroll.current = 0;
        }

        lastScrollTop.current = currentScrollTop;
      });
    };

    scrollContainer.addEventListener('scroll', handleScroll, {passive: true});

    return () => {
      isMountedRef.current = false;
      scrollContainer.removeEventListener('scroll', handleScroll);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [
    scrollContainerRef,
    threshold,
    hideDistance,
    showDistance,
    updateDirection,
  ]);

  return direction;
}

export default useScrollDirection;
